import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addPolicyDTO,
  putPolicyDTO,
  deletePolicyDTO,
} from "../../dtos/policy";
import policyService from "./policyService";

const initialState = {
  // New state properties related to policy  
  policyPayload: [] as any,
  addPolicyPayload: null,
  updatePolicyPayload: null,
  deletePayload: null,

  // Error flags for policy  
  isPolicyError: false,
  isAddPolicyError: false,
  isUpdatePolicyError: false,
  isDeletePolicyError: false,

  // Success flags for policy  
  isPolicySuccess: false,
  isAddPolicySuccess: false,
  isUpdatePolicySuccess: false,
  isDeletePolicySuccess: false,

  // Loading flags for policy  
  isPolicyLoading: false,
  isAddPolicyLoading: false,
  isUpdatePolicyLoading: false,
  isDeletePolicyLoading: false,


  // Messages for policy  
  policyMessage: "",
  addPolicyMessage: "",
  updatePolicyMessage: "",
  deletePolicyMessage: "",
};

// Async Thunks for policy   operations

// 1. add policy 
export const addPolicy = createAsyncThunk(
  "policy/ /add",
  async (policy: addPolicyDTO, thunkAPI) => {
    try {
      return await policyService.addPolicy(policy); // Call to the service for adding policy  
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// 2. Get policy
export const getPolicy = createAsyncThunk(
  "policy/ /get",
  async (id: number, thunkAPI) => {
    try {

      return await policyService.getPolicy(id); // Correct API call with `params`
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);



// update policy
export const updatePolicy = createAsyncThunk(
  "policy/ /update",
  async (policy: putPolicyDTO, thunkAPI) => {
    try {
      return await policyService.putPolicy(policy); // Call to the service for updating policy  
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// delete policy
export const deletePolicy = createAsyncThunk(
  "policy/ /delete",
  async (policy: deletePolicyDTO, thunkAPI) => {
    try {
      return await policyService.deletePolicy(policy); // Correct API call with `data` for DELETE request
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);


export const policySlice = createSlice({
  name: "policy",
  initialState,
  reducers: {
    reset: () => initialState,
    resetFlags: (state) => {
      state.isAddPolicySuccess = false;
      state.isUpdatePolicySuccess = false;
      state.isDeletePolicySuccess = false;
      state.isUpdatePolicyError = false;
      state.isDeletePolicyError = false;
      state.isAddPolicyError = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Add policy  
      .addCase(addPolicy.pending, (state) => {
        state.isAddPolicyLoading = true;
        state.isAddPolicySuccess = false;
        state.isUpdatePolicySuccess = false;
        state.isDeletePolicySuccess = false;
      })
      .addCase(addPolicy.fulfilled, (state, action) => {
        state.isAddPolicyLoading = false;
        state.isAddPolicySuccess = true;
        state.addPolicyMessage = "";
        state.addPolicyPayload = action.payload;
      })
      .addCase(addPolicy.rejected, (state, action: PayloadAction<any>) => {
        state.isAddPolicyLoading = false;
        state.isAddPolicyError = true;
        state.isAddPolicySuccess = false;
        state.addPolicyMessage = action.payload;
      })

      // Get policy  
      .addCase(getPolicy.pending, (state) => {
        state.isPolicyLoading = true;
      })
      .addCase(getPolicy.fulfilled, (state, action) => {
        state.isPolicyLoading = false;
        state.isPolicySuccess = true;
        state.policyMessage = "";
        state.policyPayload = action?.payload;
      })
      .addCase(getPolicy.rejected, (state, action: PayloadAction<any>) => {
        state.isPolicyLoading = false;
        state.isPolicyError = true;
        state.policyMessage = action.payload;
      })


      // Update policy  
      .addCase(updatePolicy.pending, (state) => {
        state.isUpdatePolicyLoading = true;
        state.isUpdatePolicySuccess = false;
        state.isDeletePolicySuccess = false;
        state.isAddPolicySuccess = false;
      })
      .addCase(updatePolicy.fulfilled, (state, action) => {
        state.isUpdatePolicyLoading = false;
        state.isUpdatePolicySuccess = true;
        state.updatePolicyMessage = "";
        state.updatePolicyPayload = action.payload;
      })
      .addCase(updatePolicy.rejected, (state, action: PayloadAction<any>) => {
        state.isUpdatePolicyLoading = false;
        state.isUpdatePolicyError = true;
        state.isUpdatePolicySuccess = false;
        state.updatePolicyMessage = action.payload;
      })

      // Delete policy  
      .addCase(deletePolicy.pending, (state) => {
        state.isDeletePolicyLoading = true;
        state.isUpdatePolicySuccess = false;
        state.isDeletePolicySuccess = false;
        state.isAddPolicySuccess = false;
      })
      .addCase(deletePolicy.fulfilled, (state, action) => {
        state.isDeletePolicyLoading = false;
        state.isDeletePolicySuccess = true;
        state.deletePolicyMessage = "";
        state.deletePayload = action.payload;
      })
      .addCase(deletePolicy.rejected, (state, action: PayloadAction<any>) => {
        state.isDeletePolicyLoading = false;
        state.isDeletePolicyError = true;
        state.isDeletePolicySuccess = false;
        state.deletePolicyMessage = action.payload;
      })
     
  },
});

export const { reset } = policySlice.actions;
export const { resetFlags } = policySlice.actions;
export default policySlice.reducer;
